// ../../private/common-types/src/non-standard-homepage-hack.ts
var isNonStandardHomepageLanguageSite = (site) => site in nonStandardHomepageDefaultLanguages;
var nonStandardHomepageDefaultLanguages = {
  "candidate-jobsdb-th": "th",
  "candidate-jobstreet-id": "id"
};

// ../../private/router-utils/src/get-lang-from-path.ts
var getLanguageFromPath = ({
  path,
  languages,
  site
}) => {
  if (path === "/" && isNonStandardHomepageLanguageSite(site)) {
    return {
      language: nonStandardHomepageDefaultLanguages[site],
      pathWithoutLanguage: "/"
    };
  }
  if (path === "/" || path.indexOf("/") !== 0 || !languages) {
    return {
      pathWithoutLanguage: path
    };
  }
  const secondSlashIndex = path.indexOf("/", 1);
  const possibleLanguage = secondSlashIndex === -1 ? path.slice(1) : path.slice(1, path.indexOf("/", 1));
  if (languages.indexOf(possibleLanguage) === -1) {
    return {
      pathWithoutLanguage: path
    };
  }
  return {
    language: possibleLanguage,
    pathWithoutLanguage: secondSlashIndex === -1 ? "/" : path.slice(secondSlashIndex)
  };
};

// ../../private/router-utils/src/parse-url.ts
import {
  getAllHostNames,
  getHostName,
  getLocale,
  getSiteNameFromHostname,
  siteConfig
} from "@seek/melways-sites";
var ensureUrlObject = (url, hostname) => {
  if (url instanceof URL) {
    return url;
  }
  try {
    const base = hostname ? `https://${hostname}` : void 0;
    return new URL(url, base);
  } catch (e) {
    if (e instanceof Error && e.message.includes("is not a valid URL") && url.startsWith("/") && !hostname) {
      throw new Error(
        "parseUrl needs a full URL including domain. Relative paths are not accepted."
      );
    }
    throw e;
  }
};
var parseUrlWithSite = ({
  url,
  site,
  staging = false
}) => {
  const hostname = getHostName({ site, staging });
  const inputUrl = ensureUrlObject(url, hostname);
  return {
    ...getMelwaysContextFromParsedValues({
      pathName: inputUrl.pathname,
      site
    }),
    staging,
    url: inputUrl,
    hostname
  };
};
var getMelwaysContextFromParsedValues = ({
  site,
  pathName
}) => {
  const config = siteConfig[site];
  const { language = "en", pathWithoutLanguage } = getLanguageFromPath({
    path: pathName,
    languages: config.languages,
    site
  });
  const locale = getLocale(site, language);
  return {
    site,
    language,
    locale,
    pathWithoutLanguage,
    classification: config.classification
  };
};

// src/providers/client.tsx
import {
  isMelwaysSiteName
} from "@seek/melways-sites";
import React, { useMemo } from "react";

// src/context.ts
import { createContext } from "react";
var MelwaysContext = createContext(null);

// src/providers/client.tsx
var MelwaysClientProvider = ({
  site,
  staging = false,
  alternative,
  children,
  path
}) => {
  if (!window) {
    throw new Error(
      "MelwaysClientProvider must be used on the client side only"
    );
  }
  if (!isMelwaysSiteName(site)) {
    throw new Error("MelwaysClientProvider requires a valid site name.");
  }
  const url = path || window.location.href;
  const value = useMemo(() => {
    const { classification, ...rest } = parseUrlWithSite({
      url,
      site,
      staging
    });
    return {
      ...rest,
      ...classification,
      ...alternative ? { alternative } : {}
    };
  }, [site, staging, alternative, url]);
  return /* @__PURE__ */ React.createElement(MelwaysContext.Provider, { value }, children);
};

// src/providers/server.tsx
import {
  isMelwaysSiteName as isMelwaysSiteName2
} from "@seek/melways-sites";
import React2, { useMemo as useMemo2 } from "react";
var MelwaysServerProvider = ({
  url,
  site,
  staging = false,
  alternative,
  children
}) => {
  if (!isMelwaysSiteName2(site)) {
    throw new Error("MelwaysServerProvider requires a valid site name.");
  }
  const contextValue = useMemo2(() => {
    const { classification, ...rest } = parseUrlWithSite({
      url,
      site,
      staging
    });
    return {
      ...rest,
      ...classification,
      ...alternative ? { alternative } : {}
    };
  }, [url, site, staging, alternative]);
  return /* @__PURE__ */ React2.createElement(MelwaysContext.Provider, { value: contextValue }, children);
};

// src/providers/static.tsx
import {
  getClassificationFromSiteName,
  getHostName as getHostName2,
  getLanguageFromLocale,
  getLocale as getLocale2
} from "@seek/melways-sites";
import React3, { useMemo as useMemo3 } from "react";
var MelwaysStaticProvider = ({
  site,
  staging = false,
  language: languageProp,
  path,
  alternative,
  children
}) => {
  const contextValue = useMemo3(() => {
    const language = getLanguageFromLocale(languageProp);
    const locale = getLocale2(site, language);
    const classification = getClassificationFromSiteName(site);
    return {
      site,
      staging,
      language,
      pathWithoutLanguage: path,
      hostname: getHostName2({ site, staging }),
      locale,
      ...classification,
      ...alternative ? { alternative } : {}
    };
  }, [site, staging, languageProp, path, alternative]);
  return /* @__PURE__ */ React3.createElement(MelwaysContext.Provider, { value: contextValue }, children);
};

// src/hooks.tsx
import { createUrlResolver } from "@seek/melways-sites";
import { useContext } from "react";
var useMelwaysInfo = () => {
  const melwaysContext = useContext(MelwaysContext);
  if (melwaysContext === null) {
    throw new Error("useMelwaysInfo must be used within a MelwaysProvider");
  }
  return melwaysContext;
};
function useMelwaysLink(linkProps) {
  const melwaysInfo = useMelwaysInfo();
  const urlResolver = createUrlResolver(melwaysInfo);
  if (!linkProps) {
    return urlResolver;
  }
  const urlStringResolver = (link) => {
    const linkToResolve = typeof link === "string" ? { path: link } : link;
    return urlResolver(linkToResolve);
  };
  if (Array.isArray(linkProps)) {
    return linkProps.map(urlStringResolver);
  }
  return urlStringResolver(linkProps);
}

// src/index.ts
var MelwaysProvider = {
  Client: MelwaysClientProvider,
  Server: MelwaysServerProvider,
  Static: MelwaysStaticProvider
};
export {
  MelwaysProvider,
  useMelwaysInfo,
  useMelwaysLink
};
//# sourceMappingURL=index.js.map